import { type DragEvent, useId, useRef, useState } from 'react'
import { cn, type FileRef, formatSize } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

// Дропзона загрузки. Развязана от shared/api: механику загрузки прокидывает
// фича через onUpload (обычно uploadFile из shared/api), здесь — только UX и
// прогресс. Байты идут в хранилище напрямую, наружу отдаётся FileRef.

type UploadState = 'uploading' | 'done' | 'error'

interface Upload {
  id: string
  name: string
  size: number
  fraction: number
  state: UploadState
}

interface FileDropProps {
  onUpload: (file: File, onProgress: (fraction: number) => void) => Promise<FileRef>
  onUploaded: (file: FileRef) => void
  accept?: string
  multiple?: boolean
  className?: string
  hint?: string
}

export function FileDrop({
  onUpload,
  onUploaded,
  accept,
  multiple = false,
  className,
  hint,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [uploads, setUploads] = useState<Upload[]>([])
  const inputId = useId()

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const list = multiple ? Array.from(files) : [files[0] as File]
    for (const file of list) {
      const id = `${file.name}:${Date.now()}:${Math.random()}`
      setUploads((prev) => [
        ...(multiple ? prev : []),
        { id, name: file.name, size: file.size, fraction: 0, state: 'uploading' },
      ])
      try {
        const ref = await onUpload(file, (fraction) =>
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, fraction } : u))),
        )
        setUploads((prev) =>
          prev.map((u) => (u.id === id ? { ...u, fraction: 1, state: 'done' } : u)),
        )
        onUploaded(ref)
      } catch {
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, state: 'error' } : u)))
      }
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDragging(false)
    void handleFiles(e.dataTransfer.files)
  }

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border border-dashed px-6 py-8 text-center transition-colors',
          dragging
            ? 'border-border-accent bg-kanban-dropzone'
            : 'border-border-strong hover:border-text-tertiary',
        )}
      >
        <Icon name="upload" size={24} className="text-text-tertiary" />
        <span className="text-sm text-text-secondary">
          Перетащите файл сюда или <span className="text-text-primary">выберите</span>
        </span>
        {hint && <span className="text-xs text-text-tertiary">{hint}</span>}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          aria-label="Выбрать файл"
          accept={accept}
          multiple={multiple}
          className="sr-only"
          onChange={(e) => {
            void handleFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </label>

      {uploads.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {uploads.map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-3 rounded-control bg-bg-elevated px-3 py-2 text-sm"
            >
              <Icon
                name={u.state === 'error' ? 'close' : u.state === 'done' ? 'check-circle' : 'clock'}
                size={16}
                className={cn(
                  'shrink-0',
                  u.state === 'error'
                    ? 'text-accent'
                    : u.state === 'done'
                      ? 'text-success'
                      : 'text-text-tertiary',
                )}
              />
              <span className="min-w-0 flex-1 truncate text-text-primary">{u.name}</span>
              {u.state === 'uploading' ? (
                <span className="mono-num shrink-0 text-xs text-text-tertiary">
                  {Math.round(u.fraction * 100)}%
                </span>
              ) : (
                <span className="mono-num shrink-0 text-xs text-text-tertiary">
                  {u.state === 'error' ? 'ошибка' : formatSize(u.size)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
